import { createResource, createSignal, on, onCleanup, onMount, Show } from "solid-js";

import ProjectList from "./ProjectList";

import Admin, { AdminMenu } from "./Admin";

import socket, { SocketStatus, SyncStatus } from "./socket";
import { useUserContext } from "./UserContext";
import { Loader, NavDropdown } from "./Components";
import UserSettings from "./UserSettings";

const JoinProject = () => {
  const [userStore, setUserStore, userActions] = useUserContext();
  const [joined, setJoined] = createSignal(false);

  onMount(() => {
    // get the url params
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    socket.emit('join-project', token);
    socket.on('joined-project', (data) => {
      setJoined(true);
      userActions.setPage('/projects/' + data.id);
    });
  });
  return (
    <div class="card-panel text-center py-5">
      <Show when={joined()} fallback={
        <div>
          <div class="spinner-border text-primary mb-3" role="status"></div>
          <h4>Rejoindre le projet...</h4>
          <p class="text-muted">Veuillez patienter pendant la validation de votre invitation.</p>
        </div>
      }>
        <div class="text-success mb-3"><i class="bi bi-check-circle-fill fs-1"></i></div>
        <h4 class="text-success">Projet rejoint avec succès !</h4>
        <p class="text-muted">Redirection en cours vers l'espace du projet...</p>
      </Show>
    </div>
  );
};

export default function App() {

  const [userStore, setUserStore, userActions] = useUserContext();

  const [log, setLog] = createSignal('');
  const [showLogs, setShowLogs] = createSignal(false);

  onMount(() => {
    socket.on('log', (data) => {
      setLog(log() + '\n' + data);
    });
    
    socket.on('project-error', (data) => {
      alert(data.message);
    });
    userActions.setPage(decodeURIComponent(window.location.pathname), {keepSearch: true});
  });

  return (
    <>
      <nav class="navbar navbar-expand-lg sticky-top custom-navbar">
        <div class="container">
          <a
            onClick={(e) => { e.preventDefault(); userActions.setPage('/'); }}
            class="navbar-brand"
            href="#"
          >
            <span class="brand-icon">
              <i class="bi bi-grid-3x3-gap-fill"></i>
            </span>
            <span>GridEval</span>
          </a>
          <button
            class="navbar-toggler border-0"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#navbarNavDropdown"
            aria-controls="navbarNavDropdown"
            aria-expanded="false"
            aria-label="Toggle navigation"
          >
            <span class="navbar-toggler-icon"></span>
          </button>
          <div class="collapse navbar-collapse" id="navbarNavDropdown">
            <ul class="navbar-nav me-auto mb-2 mb-lg-0">
              <li class="nav-item">
                <a
                  onClick={(e) => { e.preventDefault(); userActions.setPage('/'); }}
                  classList={{ "nav-link": true, active: userStore.page === '/' || userStore.page.startsWith('/projects') }}
                  href="#"
                >
                  <i class="bi bi-folder2-open me-1"></i>
                  Mes projets
                </a>
              </li>
              <Show when={userStore.role === 'admin'}>
                <AdminMenu />
              </Show>
            </ul>
            <ul class="navbar-nav align-items-lg-center">
              <Show when={userStore.username}>
                <li class="nav-item me-lg-2"><SyncStatus /></li>
                <NavDropdown
                  icon="bi-person-circle"
                  label={
                    <span class="user-badge">
                      <span class="user-avatar">{userStore.username.charAt(0)}</span>
                      <span>{userStore.username}</span>
                    </span>
                  }
                >
                  <li>
                    <a
                      onClick={(e) => { e.preventDefault(); userActions.setPage('/user-settings'); }}
                      class="dropdown-item"
                      href="#"
                    >
                      <i class="bi bi-gear me-2 text-muted"></i>
                      Paramètres & Déconnexion
                    </a>
                  </li>
                </NavDropdown>
              </Show>
            </ul>
          </div>
        </div>
      </nav>

      <main class="container main-content">
        <Show when={!userStore.username && userStore.loadingUser}>
          <div class="py-5">
            <Loader message="Authentification en cours..." />
          </div>
        </Show>

        <Show when={!userStore.username && !userStore.loadingUser}>
          <div class="row justify-content-center py-5">
            <div class="col-md-7 col-lg-5">
              <div class="card-panel text-center p-4 p-md-5">
                <div class="brand-icon mx-auto mb-3" style="width: 56px; height: 56px; font-size: 1.75rem;">
                  <i class="bi bi-grid-3x3-gap-fill"></i>
                </div>
                <h2 class="fw-bold mb-2">Bienvenue sur GridEval</h2>
                <p class="text-muted mb-4">
                  Plateforme collaborative d'évaluation et de notation d'étudiants en temps réel.
                </p>
                <button
                  type="button"
                  class="btn btn-primary btn-lg w-100 shadow-sm"
                  onClick={() => {
                    localStorage.setItem('next', location.href);
                    location.href = '/login';
                  }}
                >
                  <i class="bi bi-box-arrow-in-right me-2"></i>
                  Se connecter
                </button>
              </div>
            </div>
          </div>
        </Show>

        <Show when={userStore.username}>
          <Show when={userStore.role === 'admin'}>
            <Admin />
          </Show>
          <Show when={userStore.page === '/' || userStore.page.startsWith('/projects')}>
            <ProjectList />
          </Show>
          <Show when={userStore.page === '/user-settings'}>
            <UserSettings />
          </Show>
          <Show when={userStore.page === '/join'}>
            <JoinProject />
          </Show>

          <Show when={log()}>
            <div class="mt-4 pt-4 border-top">
              <button
                type="button"
                class="btn btn-sm btn-outline-secondary mb-2"
                onClick={() => setShowLogs(!showLogs())}
              >
                <i class={`bi ${showLogs() ? 'bi-chevron-down' : 'bi-chevron-right'} me-1`}></i>
                Console logs ({log().trim().split('\n').length})
              </button>
              <Show when={showLogs()}>
                <pre class="bg-dark text-light p-3 rounded-3 small" style="max-height: 200px; overflow-y: auto;">
                  {log()}
                </pre>
              </Show>
            </div>
          </Show>
        </Show>
      </main>

      <SocketStatus />
    </>
  );
}
